import type { ReactNode } from 'react';

type Tone = 'neutral' | 'ok' | 'warn' | 'crit' | 'info';

interface CardProps {
  title: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** simbolo/ícone à direita do título (texto curto, ex: '%' ou '↑') */
  trend?: ReactNode;
}

const toneText: Record<Tone, string> = {
  neutral: 'text-[--color-fg]',
  ok: 'text-[--color-ok]',
  warn: 'text-[--color-warn]',
  crit: 'text-[--color-crit]',
  info: 'text-[--color-info]',
};

const toneStrip: Record<Tone, string> = {
  neutral: 'tone-strip-neutral',
  ok: 'tone-strip-ok',
  warn: 'tone-strip-warn',
  crit: 'tone-strip-crit',
  info: 'tone-strip-info',
};

const toneGlow: Record<Tone, string> = {
  neutral: '',
  ok: 'card-glow-ok',
  warn: 'card-glow-warn',
  crit: 'card-glow-crit',
  info: '',
};

const sizeValue: Record<Required<CardProps>['size'], string> = {
  sm: 'text-2xl',
  md: 'text-4xl',
  lg: 'text-6xl',
  xl: 'text-[8rem]',
};

export function Card({ title, value, hint, tone = 'neutral', footer, size = 'md', trend }: CardProps) {
  return (
    <div className={`card-base p-5 transition-all duration-300 ${toneGlow[tone]}`}>
      <span className={`tone-strip ${toneStrip[tone]}`} aria-hidden />
      <div className="relative flex items-start justify-between gap-2">
        <h3 className="eyebrow">{title}</h3>
        {(hint || trend) && (
          <div className="flex items-center gap-2 text-[11px] text-[--color-fg-dim]">
            {trend}
            {hint && <span className="text-[--color-fg-muted]">{hint}</span>}
          </div>
        )}
      </div>
      <div className={`relative mt-3 font-bold leading-[0.95] tabular-nums tracking-tight ${sizeValue[size]} ${toneText[tone]}`}>
        {value}
      </div>
      {footer && <div className="relative mt-4 text-xs text-[--color-fg-dim]">{footer}</div>}
    </div>
  );
}
